<script setup lang="ts">
import { ref, computed } from "vue";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
} from "@heroicons/vue/24/outline"; 

interface Column {
  key: string;
  label: string;
  sortable?: boolean; 
  width?: string;
}

interface Props {
  columns: Column[];
  data: any[];
  loading?: boolean;
  selectable?: boolean;
  selectedIds?: string[];
  emptyMessage?: string;
  pagination?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  rowKey?: string;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  selectable: false,
  selectedIds: () => [],
  emptyMessage: "No data available",
  pagination: true,
  pageSize: 5,
  pageSizeOptions: () => [5, 10, 20, 50, 100],
  rowKey: "id",
});

const emit = defineEmits<{
  (e: "select", ids: string[]): void;
  (e: "row-click", row: any): void;
}>();


const currentPage = ref(1);
const currentPageSize = ref(props.pageSize);


const sortColumn = ref<string | null>(null);
const sortDirection = ref<"asc" | "desc">("asc");


const handleSort = (key: string) => {
  if (sortColumn.value === key) {
    if (sortDirection.value === "asc") {
      sortDirection.value = "desc";
    } else {
      
      sortColumn.value = null;
      sortDirection.value = "asc";
    }
  } else {
  
    sortColumn.value = key;
    sortDirection.value = "asc";
  }
};


const sortedData = computed(() => {
 
  if (!sortColumn.value) return props.data;

  return [...props.data].sort((a, b) => {
    const valA = a[sortColumn.value!];
    const valB = b[sortColumn.value!];

    if (valA === valB) return 0;
    if (valA === null || valA === undefined) return 1;
    if (valB === null || valB === undefined) return -1;

    if (valA < valB) return sortDirection.value === "asc" ? -1 : 1;
    if (valA > valB) return sortDirection.value === "asc" ? 1 : -1;
    return 0;
  });
});


const totalPages = computed(() =>
  Math.ceil(sortedData.value.length / currentPageSize.value),
);

const paginatedData = computed(() => {
  if (!props.pagination) return sortedData.value;

  const start = (currentPage.value - 1) * currentPageSize.value;
  const end = start + currentPageSize.value;
  return sortedData.value.slice(start, end);
});

const canGoPrevious = computed(() => currentPage.value > 1);
const canGoNext = computed(() => currentPage.value < totalPages.value);

const getRowKey = (row: any) => row[props.rowKey];

const isSelected = (row: any) => props.selectedIds.includes(getRowKey(row));

const toggleSelect = (row: any) => {
  const id = getRowKey(row);
  if (!id) return;
  const newSelection = isSelected(row)
    ? props.selectedIds.filter((i) => i !== id)
    : [...props.selectedIds, id];
  emit("select", newSelection);
};

const toggleSelectAll = () => {
  const allIds = props.data.map((row) => getRowKey(row));
  const areAllSelected =
    allIds.length > 0 && allIds.every((id) => props.selectedIds.includes(id));
  emit("select", areAllSelected ? [] : allIds);
};

const allSelectedComputed = computed(
  () =>
    props.data.length > 0 &&
    props.data.every((row) => props.selectedIds.includes(getRowKey(row))),
);

const isIndeterminateComputed = computed(() => {
  const count = props.selectedIds.length;
  return count > 0 && count < props.data.length;
});

// --- Pagination Actions ---
const changePageSize = (size: number) => {
  currentPageSize.value = size;
  currentPage.value = 1;
};

const previousPage = () => {
  if (canGoPrevious.value) currentPage.value--;
};
const nextPage = () => {
  if (canGoNext.value) currentPage.value++;
};
</script>

<template>
  <div>
    <div class="overflow-x-auto rounded-t-xl border border-b-0 border-gray-200">
      <table class="w-full">
        <thead class="border-b border-gray-200 bg-gray-50">
          <tr>
            <th v-if="selectable" class="w-10 px-4 py-3 text-left">
              <input
                type="checkbox"
                :checked="allSelectedComputed"
                :indeterminate="isIndeterminateComputed"
                @change="toggleSelectAll"
                class="h-4 w-4 cursor-pointer rounded border-gray-300 text-ram-red focus:ring-ram-red"
              />
            </th>

            <th
              v-for="column in columns"
              :key="column.key"
              :style="column.width ? { width: column.width } : {}"
              class="select-none px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
              :class="{
                'cursor-pointer hover:bg-gray-100': column.sortable !== false,
              }"
              @click="column.sortable !== false ? handleSort(column.key) : null"
            >
              <div class="flex items-center gap-1">
                <span>{{ column.label }}</span>

                <span v-if="column.sortable !== false" class="inline-block">
                  <ChevronUpIcon
                    v-if="sortColumn === column.key && sortDirection === 'asc'"
                    class="h-4 w-4 font-bold text-ram-red"
                  />
                  <ChevronDownIcon
                    v-else-if="
                      sortColumn === column.key && sortDirection === 'desc'
                    "
                    class="h-4 w-4 font-bold text-ram-red"
                  />
                  <ChevronUpDownIcon v-else class="h-4 w-4 text-gray-400" />
                </span>
              </div>
            </th>
          </tr>
        </thead>

        <tbody class="divide-y divide-gray-200 bg-white">
          <tr v-if="loading">
            <td
              :colspan="selectable ? columns.length + 1 : columns.length"
              class="px-4 py-12 text-center"
            >
              <div class="flex flex-col items-center justify-center gap-2">
                <svg
                  class="h-8 w-8 animate-spin text-ram-red"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span class="text-sm text-gray-500">Loading data...</span>
              </div>
            </td>
          </tr>

          <tr v-else-if="data.length === 0">
            <td
              :colspan="selectable ? columns.length + 1 : columns.length"
              class="px-4 py-12 text-center text-gray-500"
            >
              {{ emptyMessage }}
            </td>
          </tr>

          <tr
            v-else
            v-for="row in paginatedData"
            :key="getRowKey(row)"
            @click="emit('row-click', row)"
            class="cursor-pointer transition-colors hover:bg-gray-50"
            :class="{ 'bg-red-50': isSelected(row) }"
          >
            <td v-if="selectable" class="px-4 py-3" @click.stop>
              <input
                type="checkbox"
                :checked="isSelected(row)"
                @change="toggleSelect(row)"
                class="h-4 w-4 cursor-pointer rounded border-gray-300 text-ram-red focus:ring-ram-red"
              />
            </td>

            <td
              v-for="column in columns"
              :key="column.key"
              class="px-4 py-3 text-sm text-gray-900"
            >
              <slot
                :name="`cell-${column.key}`"
                :row="row"
                :value="row[column.key]"
              >
                {{ row[column.key] }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="pagination && data.length > 0"
      class="flex items-center justify-between rounded-b-xl border border-t-0 border-gray-200 bg-white px-4 py-3"
    >
      <div class="flex items-center gap-2">
        <label for="pageSize" class="text-sm text-gray-600"
          >Rows per page:</label
        >
        <select
          id="pageSize"
          v-model="currentPageSize"
          @change="
            changePageSize(Number(($event.target as HTMLSelectElement).value))
          "
          class="rounded-md border border-gray-300 bg-white px-2 pr-8 py-1 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ram-red appearance-none"
        >
          <option v-for="size in pageSizeOptions" :key="size" :value="size">
            {{ size }}
          </option>
        </select>
      </div>

      <div class="flex items-center gap-1">
        <button
          @click="previousPage"
          :disabled="!canGoPrevious"
          class="rounded-md p-2 text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronLeftIcon class="h-5 w-5" />
        </button>

        <span class="px-3 text-sm font-medium text-gray-700">
          Page {{ currentPage }} of {{ totalPages }}
        </span>

        <button
          @click="nextPage"
          :disabled="!canGoNext"
          class="rounded-md p-2 text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronRightIcon class="h-5 w-5" />
        </button>
      </div>
    </div>
  </div>
</template>
